import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { DataSourceProvider } from './contexts/DataSourceContext';
import { ToastProvider } from './components/ui';
import { PlayfulToastProvider } from './components/ui/PlayfulToastProvider';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminRoute from './components/auth/AdminRoute';
import TrainingRoute from './components/auth/TrainingRoute';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import LandingPage from './components/LandingPage';
import DashboardLayout from './components/layout/DashboardLayout';
import AdminLayout from './components/layout/AdminLayout';
import Dashboard from './components/dashboard/Dashboard';
import AdminDashboard from './components/admin/AdminDashboard';
import ClientAssignment from './components/admin/ClientAssignment';
import UserManagement from './components/admin/UserManagement';
import ClientsList from './components/clients/ClientsList';
import ClientForm from './components/clients/ClientForm';
import ClientDetail from './components/clients/ClientDetail';
import DealsList from './components/deals/DealsList';
import DealForm from './components/deals/DealForm';
import DealDetail from './components/deals/DealDetail';
import DealsPage from './components/deals/DealsPage';
import SalesmanQuotesPage from './components/quotes/SalesmanQuotesPage';
import TrainingDashboard from './components/training/TrainingDashboard';
import VideoManagement from './components/admin/VideoManagement';
import TrainingProgress from './components/admin/TrainingProgress';
import SuppliersList from './components/suppliers/SuppliersList';
import SupplierForm from './components/suppliers/SupplierForm';
import SupplierDetail from './components/suppliers/SupplierDetail';
import AdminAnalytics from './components/admin/AdminAnalytics';
import CompanyManagement from './components/admin/CompanyManagement';
import InventoryManagement from './components/inventory/InventoryManagement';
import CatalogueManagement from './components/catalogue/CatalogueManagement';
import CatalogueView from './components/catalogue/CatalogueView';
import ProductCataloguePage from './components/catalogue/ProductCataloguePage';
import SegmentsList from './components/segments/SegmentsList';
import SegmentDetail from './components/segments/SegmentDetail';
import EmailTemplatesList from './components/emails/EmailTemplatesList';
import EmailTemplateForm from './components/emails/EmailTemplateForm';
import EmailHistoryList from './components/emails/EmailHistoryList';
import EmailHistoryDetail from './components/emails/EmailHistoryDetail';
import SampleForm from './components/samples/SampleForm';
import SampleDetail from './components/samples/SampleDetail';
import SamplesPipeline from './components/samples/SamplesPipeline';
import BlacklistPage from './components/blacklist/BlacklistPage';
import AdminBlacklistClaims from './components/blacklist/AdminBlacklistClaims';
import FormulaStudio from './components/formulas/FormulaStudio';
import PipelineManagement from './components/admin/PipelineManagement';
import PipelineBoard from './components/deals/PipelineBoard';
import TasksList from './components/tasks/TasksList';
import TaskCommandCenter from './components/tasks/TaskCommandCenter';
import { PlansPage } from './components/plans';
import PurchaseOrdersList from './components/purchase-orders/PurchaseOrdersList';
import PurchaseOrderForm from './components/purchase-orders/PurchaseOrderForm';
import PurchaseOrderDetail from './components/purchase-orders/PurchaseOrderDetail';
import CustomerPurchaseOrdersPage from './components/purchase-orders/CustomerPurchaseOrdersPage';
import ChatPage from './components/chat/ChatPage';
import ReportsCenter from './components/reports/ReportsCenter';

function App() {
  return (
    <TooltipPrimitive.Provider delayDuration={200}>
      <AuthProvider>
        <DataSourceProvider>
          <ToastProvider>
            <PlayfulToastProvider>
              <Router>
                <div className="App">
                  <Routes>
                    {/* Public routes */}
                    <Route path="/" element={<LandingPage />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />

                    {/* Protected routes for regular users */}
                    <Route element={
                      <ProtectedRoute>
                        <DashboardLayout />
                      </ProtectedRoute>
                    }>

                      <Route path="dashboard" element={<TrainingRoute><Dashboard /></TrainingRoute>} />
                      <Route path="clients" element={<TrainingRoute><ClientsList /></TrainingRoute>} />
                      <Route path="clients/new" element={<TrainingRoute><ClientForm /></TrainingRoute>} />
                      <Route path="clients/:id" element={<TrainingRoute><ClientDetail /></TrainingRoute>} />
                      <Route path="clients/:id/edit" element={<TrainingRoute><ClientForm isEdit /></TrainingRoute>} />
                      <Route path="clients/:clientId/samples/new" element={<TrainingRoute><SampleForm /></TrainingRoute>} />
                      <Route path="clients/:clientId/samples/:id" element={<TrainingRoute><SampleDetail /></TrainingRoute>} />
                      <Route path="clients/:clientId/samples/:id/edit" element={<TrainingRoute><SampleForm /></TrainingRoute>} />
                      <Route path="samples" element={<TrainingRoute><SamplesPipeline /></TrainingRoute>} />
                      <Route path="samples/new" element={<TrainingRoute><SampleForm /></TrainingRoute>} />
                      <Route path="samples/:id" element={<TrainingRoute><SampleDetail /></TrainingRoute>} />
                      <Route path="samples/:id/edit" element={<TrainingRoute><SampleForm /></TrainingRoute>} />
                      <Route path="blacklist" element={<TrainingRoute><BlacklistPage /></TrainingRoute>} />
                      <Route path="clients/templates" element={<TrainingRoute><EmailTemplatesList /></TrainingRoute>} />
                      <Route path="clients/templates/new" element={<TrainingRoute><EmailTemplateForm /></TrainingRoute>} />
                      <Route path="clients/templates/:id/edit" element={<TrainingRoute><EmailTemplateForm /></TrainingRoute>} />
                      <Route path="clients/emails" element={<TrainingRoute><EmailHistoryList /></TrainingRoute>} />
                      <Route path="clients/emails/:id" element={<TrainingRoute><EmailHistoryDetail /></TrainingRoute>} />
                      <Route path="segments" element={<TrainingRoute><SegmentsList /></TrainingRoute>} />
                      <Route path="segments/:id" element={<TrainingRoute><SegmentDetail /></TrainingRoute>} />
                      <Route path="deals" element={<TrainingRoute><DealsPage /></TrainingRoute>} />
                      <Route path="deals/board" element={<TrainingRoute><DealsPage /></TrainingRoute>} />
                      <Route path="deals/new" element={<TrainingRoute><DealForm /></TrainingRoute>} />
                      <Route path="deals/:id" element={<TrainingRoute><DealDetail /></TrainingRoute>} />
                      <Route path="deals/:id/edit" element={<TrainingRoute><DealForm isEdit /></TrainingRoute>} />
                      <Route path="quotes" element={<TrainingRoute><SalesmanQuotesPage /></TrainingRoute>} />
                      <Route path="tasks" element={<TrainingRoute><TaskCommandCenter /></TrainingRoute>} />
                      <Route path="chat" element={<TrainingRoute><ChatPage /></TrainingRoute>} />
                      <Route path="inventory" element={<TrainingRoute><InventoryManagement /></TrainingRoute>} />
                      <Route path="invoicing" element={<TrainingRoute><CustomerPurchaseOrdersPage /></TrainingRoute>} />
                      <Route path="purchase-orders" element={<Navigate to="/invoicing" replace />} />
                      <Route path="catalogue" element={<TrainingRoute><CatalogueView /></TrainingRoute>} />
                      <Route path="catalogue/manage" element={<Navigate to="/catalogue" replace />} />
                      <Route path="training" element={<TrainingDashboard />} />
                    </Route>

                    {/* Admin routes */}
                    <Route path="/admin" element={
                      <AdminRoute>
                        <AdminLayout />
                      </AdminRoute>
                    }>
                      <Route index element={<AdminDashboard />} />
                      <Route path="analytics" element={<AdminAnalytics />} />
                      <Route path="reports" element={<ReportsCenter />} />
                      <Route path="users" element={<UserManagement />} />
                      <Route path="salesmen" element={<ClientAssignment />} />
                      <Route path="clients" element={<ClientsList />} />
                      <Route path="clients/new" element={<ClientForm />} />
                      <Route path="clients/:id" element={<ClientDetail />} />
                      <Route path="clients/:id/edit" element={<ClientForm isEdit />} />
                      <Route path="clients/:clientId/samples/new" element={<SampleForm />} />
                      <Route path="clients/:clientId/samples/:id" element={<SampleDetail />} />
                      <Route path="clients/:clientId/samples/:id/edit" element={<SampleForm />} />
                      <Route path="samples" element={<SamplesPipeline />} />
                      <Route path="samples/new" element={<SampleForm />} />
                      <Route path="samples/:id" element={<SampleDetail />} />
                      <Route path="samples/:id/edit" element={<SampleForm />} />
                      <Route path="blacklist" element={<AdminBlacklistClaims />} />
                      <Route path="formulas" element={<FormulaStudio />} />
                      <Route path="clients/templates" element={<EmailTemplatesList />} />
                      <Route path="clients/templates/new" element={<EmailTemplateForm />} />
                      <Route path="clients/templates/:id/edit" element={<EmailTemplateForm />} />
                      <Route path="clients/emails" element={<EmailHistoryList />} />
                      <Route path="clients/emails/:id" element={<EmailHistoryDetail />} />
                      <Route path="deals" element={<DealsPage />} />
                      <Route path="deals/board" element={<DealsPage />} />
                      <Route path="deals/new" element={<DealForm />} />
                      <Route path="deals/:id" element={<DealDetail />} />
                      <Route path="deals/:id/edit" element={<DealForm isEdit />} />
                      <Route path="quotes" element={<SalesmanQuotesPage />} />
                      <Route path="tasks" element={<TaskCommandCenter />} />
                      <Route path="chat" element={<ChatPage />} />
                      <Route path="pipelines" element={<PipelineManagement />} />
                      <Route path="training/videos" element={<VideoManagement />} />
                      <Route path="training/progress" element={<TrainingProgress />} />
                      <Route path="suppliers" element={<SuppliersList />} />
                      <Route path="suppliers/new" element={<SupplierForm />} />
                      <Route path="suppliers/:id" element={<SupplierDetail />} />
                      <Route path="suppliers/:id/edit" element={<SupplierForm />} />
                      <Route path="companies" element={<CompanyManagement />} />
                      <Route path="inventory" element={<InventoryManagement />} />
                      <Route path="catalogue" element={<CatalogueManagement />} />
                      <Route path="catalogue/:productId" element={<ProductCataloguePage />} />
                      <Route path="catalogue/:productId/docs" element={<ProductCataloguePage />} />
                      <Route path="segments" element={<SegmentsList />} />
                      <Route path="segments/:id" element={<SegmentDetail />} />
                      <Route path="plans" element={<PlansPage />} />
                      <Route path="purchase-orders" element={<PurchaseOrdersList />} />
                      <Route path="purchase-orders/new" element={<PurchaseOrderForm />} />
                      <Route path="purchase-orders/:id" element={<PurchaseOrderDetail />} />
                      <Route path="purchase-orders/:id/edit" element={<PurchaseOrderForm isEdit />} />
                    </Route>

                    {/* Catch all route */}
                    <Route path="*" element={<Navigate to="/dashboard" replace />} />
                  </Routes>
                </div>
              </Router>
            </PlayfulToastProvider>
          </ToastProvider>
        </DataSourceProvider>
      </AuthProvider>
    </TooltipPrimitive.Provider>
  );
}

export default App;
